"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function StartupHero() {
  const shouldReduceMotion = useReducedMotion();
  const benefits = ["Direct 1-on-1 strategy with Yassine Essaadi", "Pragmatic technical setups tailored for early MVPs", "Targeted keyword maps for initial buyer capture", "Zero agency middle management or bloated retainers"];
  const statBadges = [{
    label: "Direct consultation",
    desc: "No junior account managers"
  }, {
    label: "Technical readiness",
    desc: "Clean architecture from launch"
  }, {
    label: "Sustainable pipeline",
    desc: "Zero black-hat shortcuts"
  }];
  return <section id="startuphero" data-nav="light" className="relative overflow-hidden bg-background text-foreground pt-12 pb-20 md:pt-20 md:pb-28 border-b border-border">
      {/* Subtle background ambient accents */}
      <div className="absolute -top-32 right-1/2 translate-x-1/2 w-96 h-96 bg-secondary/60 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute top-1/4 -right-20 w-80 h-80 bg-primary/5 rounded-full blur-2xl pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Copy Column */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }}>
              <Badge variant="secondary" className="mb-5 px-3 py-1 font-medium tracking-normal text-xs uppercase border border-border flex items-center gap-1.5">
                <Sparkles className="size-3.5 text-primary" />
                <span>Early-stage startup search foundations</span>
              </Badge>
            </motion.div>

            <motion.h1 initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.08
          }} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif leading-tight">
              Build your startup&apos;s search foundation from day one
            </motion.h1>

            <motion.p initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.16
          }} className="mt-6 text-lg sm:text-xl text-muted-foreground font-sans max-w-2xl leading-relaxed">
              Work directly with me to design clean technical architecture, rank for
              high-intent customer searches, and grow organic traction without wasting
              months on generic advice.
            </motion.p>

            {/* Value checklist */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.24
          }} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl">
              {benefits.map((item, index) => <div key={index} data-index={index} className="flex items-start gap-2.5 text-sm font-medium text-foreground/90">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>{item}</span>
                </div>)}
            </motion.div>

            {/* CTAs */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.32
          }} className="mt-10 flex flex-col sm:flex-row gap-4 w-full sm:w-auto items-stretch sm:items-center">
              <Button asChild size="lg" className="rounded-md font-medium text-base px-6">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md font-medium text-base px-6 bg-background hover:bg-muted">
                <Link to="/CaseStudiesPage">Explore my case studies</Link>
              </Button>
            </motion.div>

            {/* Trust reassurance note */}
            <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span>Personal engagement. You speak and strategise directly with Yassine.</span>
            </div>
          </div>

          {/* Portrait & Profile Card Column */}
          <div className="lg:col-span-5 flex flex-col items-center">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="w-full max-w-md bg-card rounded-2xl border border-border p-6 shadow-xl relative">
              {/* Profile Image container */}
              <div className="relative rounded-xl overflow-hidden aspect-4/5 bg-muted mb-6 border border-border/80">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Independent Freelance SEO Consultant" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent p-4 text-white">
                  <p className="font-semibold text-base font-serif text-white">Yassine Essaadi</p>
                  <p className="text-xs text-white/90 font-sans">
                    Independent Freelance SEO Consultant &bull; YE Consultancy
                  </p>
                </div>
              </div>

              {/* Startup Principles cards */}
              <div className="grid grid-cols-1 gap-2.5">
                {statBadges.map((stat, i) => <div key={i} data-index={i} className="flex flex-col p-3 rounded-lg bg-background border border-border text-left">
                    <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                      {stat.label}
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {stat.desc}
                    </span>
                  </div>)}
              </div>

              <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>Available for UK &amp; International startups</span>
                <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                  <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                  Direct bookings open
                </span>
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}